import { useEffect, useRef, useState } from "react";
import { SELECTED_FACILITY_STORAGE_KEY } from "../helpers/sessionScopedStorage";

/** Établissement vers lequel un AUTRE onglet a fait basculer le stockage partagé. */
export interface CrossTabFacilityChange {
    /** Id désormais envoyé en `X-Facility-Id` par cet onglet aussi. */
    facilityId: number;
}

interface ObservedChange {
    facilityId: number;
    /** Établissement affiché par CET onglet au moment de l'événement. */
    observedFrom: number;
}

function hasAccessToken(): boolean {
    try {
        return Boolean(localStorage.getItem("ACCESS_TOKEN"));
    } catch {
        return false;
    }
}

/**
 * L'établissement affiché est un état React propre à l'onglet, mais l'en-tête
 * `X-Facility-Id` est relu à chaque requête dans `localStorage`, partagé par tous
 * les onglets. Quand un autre onglet bascule d'établissement, celui-ci continue
 * d'afficher l'ancien tout en lisant et en écrivant dans le nouveau.
 *
 * Renvoie le changement tant que le stockage pointe ailleurs que l'établissement
 * affiché, `null` sinon. L'événement `storage` ne se déclenche que dans les AUTRES
 * onglets : une bascule faite ici (qui écrit elle-même la clé) ne remonte jamais.
 *
 * Clé retirée : avec un jeton encore présent, le serveur retombe sur
 * l'établissement du compte (`defaultFacilityId`). Sans jeton, c'est une
 * déconnexion, que le 401 suivant traite déjà : on ne fait rien.
 */
export default function useCrossTabFacilityChange(
    currentFacilityId: number | null | undefined,
    defaultFacilityId: number | null | undefined,
): CrossTabFacilityChange | null {
    const [observed, setObserved] = useState<ObservedChange | null>(null);
    const currentRef = useRef(currentFacilityId);
    const defaultRef = useRef(defaultFacilityId);
    currentRef.current = currentFacilityId;
    defaultRef.current = defaultFacilityId;

    useEffect(() => {
        const handleStorage = (event: StorageEvent) => {
            if (event.key !== SELECTED_FACILITY_STORAGE_KEY) return;

            const current = currentRef.current;
            if (!current) return;

            let target: number | null;
            if (event.newValue === null) {
                if (!hasAccessToken()) return;
                target = defaultRef.current ?? null;
            } else {
                target = Number(event.newValue);
            }

            if (!target || !Number.isInteger(target)) return;

            setObserved({ facilityId: target, observedFrom: current });
        };

        window.addEventListener("storage", handleStorage);
        return () => window.removeEventListener("storage", handleStorage);
    }, []);

    // Ne vaut que pour l'établissement affiché lors de l'événement : une bascule
    // faite ensuite dans cet onglet (ou un retour à l'identique dans l'autre)
    // rend l'observation caduque sans effet de remise à zéro.
    if (
        !observed
        || observed.observedFrom !== currentFacilityId
        || observed.facilityId === currentFacilityId
    ) {
        return null;
    }

    return { facilityId: observed.facilityId };
}
